import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  PopoverBody,
  UncontrolledPopover,
} from 'reactstrap';
import { Option, OptionDataValue, OptionDataValueInput } from '../../shared/options';
import { GenericOptionInput } from '../../shared/orders';
import { useTranslation } from '../translations';
import { classNames, setElementInArray, useConfig } from '../utils';
import { SelectBlocks } from './SelectBlock';
import { OptionAddressComponent } from './option/OptionAddressComponent';
import { OptionFileComponent, OptionFileProps } from './option/OptionFileComponent';
import { OptionPhoneComponent } from './option/OptionPhoneComponent';

interface ProductOptionViewerProps {
  readonly disabled?: boolean;
  readonly option: GenericOptionInput;
  readonly defaultValue?: OptionDataValue;
  readonly value?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
  readonly adminView?: boolean;
}

export const ProductOptionViewer: FC<ProductOptionViewerProps> = ({
  disabled,
  option,
  onChange,
  defaultValue,
  value,
  adminView,
}) => {
  const { ts } = useTranslation();

  return (
    <FormGroup>
      <Label>
        {option.name}{' '}
        <span className="badge-list small compact">
          {option.required && <Badge color="danger">{ts('common.required')}</Badge>}{' '}
          {!option.mutable && (
            <>
              <Badge color="info" id="mutableNotice">
                {ts('option.immutable')}
              </Badge>{' '}
              <UncontrolledPopover placement="top" target="mutableNotice" trigger="hover">
                <PopoverBody>
                  {ts(
                    adminView
                      ? 'option.mutability.explanation.staff'
                      : 'option.mutability.explanation.user',
                  )}
                </PopoverBody>
              </UncontrolledPopover>
            </>
          )}
        </span>
      </Label>
      <ProductOptionRenderer
        defaultValue={defaultValue}
        disabled={disabled}
        onChange={onChange}
        option={option}
        required={option.required}
        value={value}
      />
      {option.description && <FormText color="muted">{option.description}</FormText>}
    </FormGroup>
  );
};

interface ProductOptionRendererProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValue;
  readonly required: boolean;
  readonly value?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

export const ProductOptionRenderer: FC<ProductOptionRendererProps> = (props) => {
  const { currencySymbol } = useConfig();
  const { disabled, option, required, onChange, defaultValue, value } = props;

  if (option.type === 'select') {
    return <POSingleSelect {...props} />;
  }

  if (option.type === 'multi') {
    return <POMultiSelect {...props} />;
  }

  if (option.type === 'text') {
    return <OptionText {...props} />;
  }

  if (option.type === 'file') {
    return <OptionFileComponent {...(props as OptionFileProps)} />;
  }

  if (option.type === 'phone') {
    return <OptionPhoneComponent {...props} />;
  }

  if (option.type === 'address') {
    return <OptionAddressComponent {...props} />;
  }

  return (
    <InputGroup>
      {option.type === 'currency' && <InputGroupText>{currencySymbol}</InputGroupText>}
      <Input
        defaultValue={(defaultValue as string | undefined) ?? '0'}
        disabled={disabled}
        id={`option${option.id}`}
        max={
          (option.type === 'currency' && option.max ? option.max / 100 : option.max) ?? undefined
        }
        min={option.type === 'currency' ? option.min / 100 : option.min}
        name={`options[${option.id}]`}
        onChange={(e) => {
          const v = e.currentTarget.value;
          onChange?.(option.id, v === '' ? null : v);
        }}
        /*
         * I am so sorry for this horrible logic but it's warranted here.
         * This will not be allowed when being redone for dynamic forms.
         * This logic checks the following:
         *  - If the required parameter is present: mark required.
         *  - If the required field is NOT present: check first that the option is NOT a currency.
         *  - If it is a currency: do not mark required since the required field was not provided.
         *  - If it is NOT a currency: Is there a minimum? If yes, then mark it required as it CAN NOT be empty.
         *  - If the minimum is 0, then it can be empty, and it does not need to be marked as required.
         */
        required={required ? true : option.type !== 'currency' && option.min > 0 ? true : undefined}
        step={option.type === 'currency' ? 0.01 : undefined}
        type="number"
        value={value as string}
      />
    </InputGroup>
  );
};

interface OptionTextProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValueInput;
  readonly required: boolean;
  readonly value?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const OptionText: FC<OptionTextProps> = ({
  disabled,
  option,
  required,
  defaultValue,
  value,
  onChange,
}) => {
  const onChangeCallback = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value;
      onChange?.(option.id, v === '' ? null : v);
    },
    [onChange, option],
  );

  const maxLength = option.max && option.max > option.min ? option.max : undefined;

  return (
    <InputGroup>
      <Input
        defaultValue={(defaultValue as string | undefined) ?? ''}
        disabled={disabled}
        id={`option${option.id}`}
        maxLength={maxLength}
        minLength={option.min}
        name={`options[${option.id}]`}
        onChange={onChangeCallback}
        required={required}
        type={option.style === 'textarea' ? 'textarea' : 'text'}
        value={value as string}
      />
    </InputGroup>
  );
};

interface POSingleSelectProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValue;
  readonly required: boolean;
  readonly value?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const POSingleSelect: FC<POSingleSelectProps> = ({
  disabled,
  option,
  required,
  onChange,
  defaultValue,
  value,
}) => {
  if (option.style === 'radio') {
    return (
      <POSingleRadio
        defaultValue={defaultValue ?? value}
        disabled={disabled}
        onChange={onChange}
        option={option}
      />
    );
  }

  return (
    <POSingleSelectDropdown
      defaultValue={defaultValue ?? value}
      disabled={disabled}
      onChange={onChange}
      option={option}
      required={required}
    />
  );
};

interface POSingleRadioProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const POSingleRadio: FC<POSingleRadioProps> = ({ disabled, option, defaultValue, onChange }) => {
  const name = `options[${option.id}]`;

  const onChangeCallback = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      onChange?.(option.id, value === '' ? null : value);
    },
    [onChange, option],
  );

  return (
    <FormGroup>
      {(option.selectValues ?? []).map((opt) => {
        const id = `option${option.id}-${opt.value}`;
        return (
          <FormGroup check className="mt-10" key={opt.value}>
            <Input
              defaultChecked={(defaultValue as string | undefined) === opt.value}
              disabled={disabled}
              id={id}
              name={name}
              onChange={onChangeCallback}
              type="radio"
              value={opt.value}
            />
            <Label check for={id}>
              {opt.displayName ?? opt.value}
            </Label>
          </FormGroup>
        );
      })}
    </FormGroup>
  );
};

interface POSingleSelectDropdownProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValueInput;
  readonly required: boolean;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const POSingleSelectDropdown: FC<POSingleSelectDropdownProps> = ({
  disabled,
  option,
  required,
  onChange,
  defaultValue,
}) => {
  const { currency, ts } = useTranslation();
  const name = `options[${option.id}]`;

  const callback = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      onChange?.(option.id, value === '' ? null : value);
    },
    [option, onChange],
  );

  return (
    <FormGroup>
      <Input
        data-coerce={false}
        defaultValue={(defaultValue as string | undefined) ?? ''}
        disabled={disabled}
        id={`option${option.id}`}
        name={disabled ? undefined : name}
        onChange={callback}
        required={required}
        type="select"
      >
        {required ? (
          <option disabled value="">
            {ts('required_select_one')}
          </option>
        ) : (
          <option value="">{ts('none')}</option>
        )}
        {(option.selectValues ?? []).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.displayName ?? opt.value}{' '}
            {opt.priceModifier > 0 && `(${currency(opt.priceModifier)})`}
          </option>
        ))}
      </Input>
    </FormGroup>
  );
};

interface POMultiSelectProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const POMultiSelect: FC<POMultiSelectProps> = ({ disabled, option, onChange, defaultValue }) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    Array.isArray(defaultValue) ? (defaultValue as string[]) : [],
  );

  useEffect(() => {
    onChange?.(option.id, [...new Set(selectedOptions)]);
  }, [selectedOptions, onChange, option.id]);

  return (
    <FormGroup id={`option${option.id}`}>
      {option.style === 'simple' ? (
        <MultipleSelectCheckboxes
          disabled={disabled}
          option={option}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
        />
      ) : (
        <MultipleSelectBlocks
          disabled={disabled}
          option={option}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
        />
      )}
      {selectedOptions.map((id) => (
        <Input
          data-coerce={false}
          key={id}
          name={`options[${option.id}][]`}
          type="hidden"
          value={id}
        />
      ))}
    </FormGroup>
  );
};

interface MultipleSelectCheckboxesProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly selectedOptions: string[];
  readonly setSelectedOptions: (options: string[]) => void;
}

const MultipleSelectCheckboxes: FC<MultipleSelectCheckboxesProps> = ({
  option,
  selectedOptions,
  setSelectedOptions,
  disabled,
}) => {
  const selectValues = option.selectValues ?? [];
  const { currency } = useTranslation();

  const changeCallback = useCallback(
    (key: string, checked: boolean) => {
      setSelectedOptions(setElementInArray(selectedOptions, key, checked));
    },
    [selectedOptions, setSelectedOptions],
  );

  return selectValues.map((value) => {
    const id = `optionValue${value.value}`;
    const name = value.displayName ?? value.value;
    const modifier = value.priceModifier === 0 ? '' : ` (${currency(value.priceModifier)})`;

    const selected = selectedOptions.includes(value.value);

    return (
      <FormGroup check className="mt-10" key={id}>
        <Input
          checked={selected}
          className={classNames({ selected })}
          disabled={disabled}
          id={id}
          onChange={(e) => {
            changeCallback(value.value, e.target.checked);
          }}
          type="checkbox"
        />
        <Label check for={id}>
          {`${name}${modifier}`}
        </Label>
      </FormGroup>
    );
  });
};

interface MultipleSelectBlocksProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly selectedOptions: string[];
  readonly setSelectedOptions: (option: string[]) => void;
}

const MultipleSelectBlocks: FC<MultipleSelectBlocksProps> = ({
  option,
  selectedOptions,
  setSelectedOptions,
  disabled,
}) => {
  const { currency } = useTranslation();
  const selectValues = option.selectValues ?? [];

  return (
    <SelectBlocks
      allowMultiple
      defaultSelectedIds={selectedOptions.map((t) => selectValues.findIndex((u) => u.value === t))}
      disabled={disabled}
      items={selectValues.map(({ displayName, value, priceModifier }) => {
        const name = displayName ?? value;
        const modifier = priceModifier === 0 ? '' : ` (${currency(priceModifier)})`;
        return { name: `${name}${modifier}`, id: `optionValue${value}` };
      })}
      max={option.max}
      onChange={(idxs) => {
        setSelectedOptions(idxs.map((id) => selectValues[id]!.value));
      }}
      ordered={option.style === 'ordered'}
      textOnly
    />
  );
};
